// Add your own custom JS here.

if (!customElements.get('portrait-personalization')) {
  customElements.define('portrait-personalization', class extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.variantPicker = document.getElementById(`VariantPickers-${this.dataset.sectionId}`);
      this.configurationError = this.querySelector('[data-portrait-configuration-error]');
      this.referenceInput = this.querySelector('[data-portrait-reference]');
      this.variants = JSON.parse(document.getElementById(`ProductVariantData-${this.dataset.sectionId}`).textContent);
      this.minPeople = Number(this.dataset.minPeople);
      this.maxPeople = Number(this.dataset.maxPeople);
      this.animalValue = this.dataset.animalValue || 'Animal';

      this.handleVariantChange = this.onVariantChange.bind(this);
      this.handleLocalChange = this.onLocalChange.bind(this);
      this.handleInput = this.onInput.bind(this);
      this.handleSubmit = this.onSubmit.bind(this);

      document.addEventListener('change', this.handleVariantChange);
      this.addEventListener('change', this.handleLocalChange);
      this.addEventListener('input', this.handleInput);
      if (this.form) this.form.addEventListener('submit', this.handleSubmit, true);

      this.syncFromVariant();
      this.querySelectorAll('[data-portrait-text-input]').forEach(input => this.updateCharacterCount(input));
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.handleVariantChange);
      this.removeEventListener('change', this.handleLocalChange);
      this.removeEventListener('input', this.handleInput);
      if (this.form) this.form.removeEventListener('submit', this.handleSubmit, true);
    }

    onVariantChange(event) {
      if (!this.variantPicker || !this.variantPicker.contains(event.target)) return;
      this.syncFromVariant();
    }

    onLocalChange(event) {
      if (!event.target.matches('[data-portrait-character]')) return;
      this.syncCharacter(event.target.closest('[data-portrait-field]'), true);
    }

    onInput(event) {
      if (event.target.matches('[data-portrait-text-input]')) {
        this.updateCharacterCount(event.target);
      }
    }

    onSubmit(event) {
      const isConfigured = this.syncFromVariant();
      const variant = this.getSelectedVariant();
      const variantInput = this.form && this.form.querySelector('[name="id"]');
      const matchesVariant = variant && variant.available && variantInput && String(variant.id) === variantInput.value;

      if (isConfigured && matchesVariant && this.form.checkValidity()) {
        this.referenceInput.value = this.createReference();
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      if (!isConfigured || !matchesVariant) {
        this.configurationError.classList.remove('hidden');
        this.configurationError.scrollIntoView({ block: 'center' });
      } else {
        this.form.reportValidity();
      }

      return;
    }

    createReference() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }

      return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    syncFromVariant() {
      const peopleCount = this.getSelectedPeopleCount();
      const isConfigured = Boolean(this.getSelectedVariant()) && Number.isInteger(peopleCount) &&
        peopleCount >= this.minPeople && peopleCount <= this.maxPeople;

      this.configurationError.classList.toggle('hidden', isConfigured);

      this.querySelectorAll('[data-portrait-field]').forEach(field => {
        const character = field.querySelector('[data-portrait-character]');
        const name = field.querySelector('[data-portrait-name]');
        const isVisible = isConfigured && Number(field.dataset.portraitField) <= peopleCount;

        field.hidden = !isVisible;
        character.disabled = !isVisible;
        character.required = isVisible;
        name.disabled = !isVisible;
        name.required = isVisible;
        this.syncCharacter(field, isVisible);
      });

      return isConfigured;
    }

    getSelectedPeopleCount() {
      if (!this.variantPicker) return NaN;

      const optionPosition = Number(this.dataset.optionPosition) - 1;
      const controls = Array.from(this.variantPicker.querySelectorAll('loess-variant-selects, loess-variant-radios'));
      const control = controls[optionPosition];

      if (!control || typeof control.getOptionValue !== 'function') return NaN;

      const optionValue = String(control.getOptionValue() || '').trim();

      return /^\d+$/.test(optionValue) ? Number(optionValue) : NaN;
    }

    getSelectedVariant() {
      if (!this.variantPicker) return null;
      const controls = Array.from(this.variantPicker.querySelectorAll('loess-variant-selects, loess-variant-radios'));
      const options = controls.map(control => control.getOptionValue());

      return this.variants.find(variant => variant.options.length === options.length &&
        variant.options.every((option, index) => option === options[index]));
    }

    syncCharacter(field, fieldIsVisible) {
      const character = field.querySelector('[data-portrait-character]');
      const animalField = field.querySelector('[data-portrait-animal-field]');
      const animalInput = field.querySelector('[data-portrait-animal-input]');
      const isAnimal = fieldIsVisible && character.value === this.animalValue;

      if (animalField) animalField.hidden = !isAnimal;
      if (animalInput) {
        animalInput.disabled = !isAnimal;
        animalInput.required = isAnimal;
      }
    }

    updateCharacterCount(input) {
      const counter = input.closest('[data-portrait-text-control]').querySelector('[data-portrait-character-count]');
      counter.textContent = `${input.value.length}/${input.maxLength}`;
    }
  });
}

if (!customElements.get('engraving-personalization')) {
  customElements.define('engraving-personalization', class extends HTMLElement {
    constructor() {
      super();
      this.maxFileSize = 10 * 1024 * 1024;
      this.maxPixels = 20_000_000;
      this.fileValidationVersions = new WeakMap();
      this.previewUrls = new Map();
      this.emojiPattern = /[\u{1F000}-\u{1FAFF}\uFE0F]/u;
    }

    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.variantPicker = document.getElementById(`VariantPickers-${this.dataset.sectionId}`);
      this.fields = this.querySelector('[data-engraving-fields]');
      this.configurationError = this.querySelector('[data-configuration-error]');
      this.referenceInput = this.querySelector('[data-engraving-reference]');
      this.quantityInput = this.querySelector('[data-engraving-quantity]');
      this.instructions = this.querySelector('[data-engraving-instructions]');
      this.selectionValue = this.variantPicker && this.variantPicker.querySelector('[data-engraving-selection-value]');

      this.handleLocalChange = this.onLocalChange.bind(this);
      this.handleLocalClick = this.onLocalClick.bind(this);
      this.handleLocalInput = this.onLocalInput.bind(this);
      this.handleVariantChange = this.onVariantChange.bind(this);
      this.handleSubmit = this.onSubmit.bind(this);

      this.addEventListener('change', this.handleLocalChange);
      this.addEventListener('click', this.handleLocalClick);
      this.addEventListener('input', this.handleLocalInput);
      document.addEventListener('change', this.handleVariantChange);
      if (this.form) this.form.addEventListener('submit', this.handleSubmit, true);

      this.syncFromVariant();
      this.querySelectorAll('[data-engraving-side]').forEach(side => this.updateFontPicker(side));
      this.updateInstructionsCount();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleLocalChange);
      this.removeEventListener('click', this.handleLocalClick);
      this.removeEventListener('input', this.handleLocalInput);
      document.removeEventListener('change', this.handleVariantChange);
      if (this.form) this.form.removeEventListener('submit', this.handleSubmit, true);

      this.previewUrls.forEach(url => URL.revokeObjectURL(url));
      this.previewUrls.clear();
    }

    onVariantChange(event) {
      if (!this.variantPicker || !this.variantPicker.contains(event.target)) return;
      this.syncFromVariant();
    }

    onLocalChange(event) {
      if (event.target.matches('[data-engraving-type]')) {
        this.syncSide(event.target.closest('[data-engraving-side]'));
      }

      if (event.target.matches('[data-font-select]')) {
        const side = event.target.closest('[data-engraving-side]');
        this.validateFont(event.target);
        this.updateFontPicker(side);
        this.updateFontPreview(side);
      }

      if (event.target.matches('[data-engraving-file]')) {
        this.updateFileName(event.target);
        this.validateFile(event.target);
      }
    }

    onLocalClick(event) {
      const option = event.target.closest('[data-font-option]');
      if (!option || !this.contains(option)) return;

      const side = option.closest('[data-engraving-side]');
      const select = side.querySelector('[data-font-select]');
      const picker = option.closest('[data-font-picker]');

      select.value = option.dataset.fontOption;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      picker.open = false;
      picker.querySelector('[data-font-trigger]').focus();
    }

    onLocalInput(event) {
      if (event.target.matches('[data-engraving-text]')) {
        this.validateText(event.target);
        this.updateCharacterCount(event.target);
      }

      if (event.target.closest('[data-engraving-instructions]')) {
        this.updateInstructionsCount();
      }
    }

    onSubmit(event) {
      this.syncFromVariant();
      this.querySelectorAll('[data-engraving-text]:not([disabled])').forEach(input => this.validateText(input));
      const enabledFonts = Array.from(this.querySelectorAll('[data-font-select]:not([disabled])'));
      enabledFonts.forEach(input => this.validateFont(input, true));
      const invalidFont = enabledFonts.find(input => !input.value);

      if (!this.form || !this.form.checkValidity()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (invalidFont) {
          invalidFont.closest('[data-font-field]').querySelector('[data-font-trigger]').focus();
        } else if (this.form) {
          this.form.reportValidity();
        }
        return;
      }

      this.quantityInput.value = '1';
      this.referenceInput.value = this.createReference();
    }

    syncFromVariant() {
      const configured = this.dataset.configured === 'true' && this.variantPicker;
      const selectedValue = configured ? this.getSelectedEngravingValue() : null;
      const mode = this.getMode(selectedValue);
      const hasEngraving = mode === 'one' || mode === 'two';

      if (this.selectionValue && selectedValue !== null) {
        this.selectionValue.textContent = selectedValue;
      }

      this.hidden = configured && mode === 'blank';

      this.configurationError.classList.toggle('hidden', configured && mode !== 'invalid');
      this.fields.hidden = !hasEngraving;

      const sideOne = this.querySelector('[data-engraving-side="1"]');
      const sideTwo = this.querySelector('[data-engraving-side="2"]');

      this.setGroupEnabled(sideOne, hasEngraving);
      this.setGroupEnabled(sideTwo, mode === 'two');
      sideOne.hidden = !hasEngraving;
      sideTwo.hidden = mode !== 'two';

      this.setGroupEnabled(this.instructions, hasEngraving);
      this.instructions.hidden = !hasEngraving;

      if (hasEngraving) this.syncSide(sideOne);
      if (mode === 'two') this.syncSide(sideTwo);

      if (!configured || mode === 'invalid') {
        const addButton = this.form && this.form.querySelector('[name="add"]');
        if (addButton) addButton.disabled = true;
      }
    }

    getSelectedEngravingValue() {
      const optionPosition = Number(this.dataset.optionPosition) - 1;
      const controls = Array.from(this.variantPicker.querySelectorAll('loess-variant-selects, loess-variant-radios'));
      const control = controls[optionPosition];

      if (!control || typeof control.getOptionValue !== 'function') return null;
      return control.getOptionValue();
    }

    getMode(value) {
      if (value === null) return 'invalid';
      if (value === this.dataset.blankValue) return 'blank';
      if (value === this.dataset.oneSideValue) return 'one';
      if (value === this.dataset.twoSidesValue) return 'two';
      return 'invalid';
    }

    setGroupEnabled(group, enabled) {
      if (!group) return;

      group.querySelectorAll('input, select, textarea').forEach(input => {
        input.disabled = !enabled;
        if (!enabled) {
          input.required = false;
        }
      });
    }

    syncSide(side) {
      if (!side || side.hidden) return;

      const typeInputs = Array.from(side.querySelectorAll('[data-engraving-type]'));
      const selectedType = typeInputs.find(input => input.checked) || typeInputs[0];
      const textWrapper = side.querySelector('[data-text-input]');
      const fileWrapper = side.querySelector('[data-file-input]');
      const textInput = side.querySelector('[data-engraving-text]');
      const fileInput = side.querySelector('[data-engraving-file]');
      const fontSelect = side.querySelector('[data-font-select]');
      const usesText = selectedType && selectedType.value === 'Text';

      typeInputs.forEach(input => {
        input.disabled = false;
        input.required = true;
      });
      textWrapper.hidden = !usesText;
      fileWrapper.hidden = usesText;
      textInput.disabled = !usesText;
      textInput.required = usesText;
      fontSelect.disabled = !usesText;
      fontSelect.required = usesText;
      fileInput.disabled = usesText;
      fileInput.required = !usesText;

      if (usesText) {
        this.validateText(textInput);
        this.validateFont(fontSelect);
        this.updateFontPicker(side);
        this.updateFontPreview(side);
      } else {
        this.validateFont(fontSelect);
        if (fileInput.files.length === 0) fileInput.setCustomValidity('');
      }
    }

    validateText(input) {
      const hasEmoji = this.emojiPattern.test(input.value);
      const message = hasEmoji ? this.dataset.emojiMessage : '';
      input.setCustomValidity(message);
      input.closest('[data-text-input]').querySelector('[data-text-error]').textContent = message;
    }

    validateFont(select, showMessage = false) {
      const message = select.value ? '' : this.dataset.fontRequiredMessage;
      const trigger = select.closest('[data-font-field]').querySelector('[data-font-trigger]');
      select.setCustomValidity(message);
      select.closest('[data-font-field]').querySelector('[data-font-error]').textContent = showMessage ? message : '';
      trigger.setAttribute('aria-invalid', message && showMessage ? 'true' : 'false');
    }

    updateFontPicker(side) {
      const select = side.querySelector('[data-font-select]');
      const picker = side.querySelector('[data-font-picker]');
      if (!select || !picker) return;

      const font = select.value;
      picker.querySelector('[data-font-trigger-label]').textContent = font || select.dataset.fontPlaceholder;

      if (font) {
        picker.dataset.selectedFont = font;
      } else {
        delete picker.dataset.selectedFont;
      }

      picker.querySelectorAll('[data-font-option]').forEach(option => {
        option.setAttribute('aria-selected', option.dataset.fontOption === font ? 'true' : 'false');
      });
    }

    updateFontPreview(side) {
      const font = side.querySelector('[data-font-select]').value;
      if (font) {
        side.dataset.selectedFont = font;
      } else {
        delete side.dataset.selectedFont;
      }
    }

    updateCharacterCount(input) {
      input.closest('[data-text-input]').querySelector('[data-character-count]').textContent = `${input.value.length}/${input.maxLength}`;
    }

    updateFileName(input) {
      const name = input.files[0] ? input.files[0].name : input.dataset.noFileLabel;
      input.closest('[data-file-input]').querySelector('[data-file-name]').textContent = name;
    }

    updateInstructionsCount() {
      if (!this.instructions) return;
      const input = this.instructions.querySelector('textarea');
      this.instructions.querySelector('[data-instructions-count]').textContent = `${input.value.length}/250`;
    }

    async validateFile(input) {
      const file = input.files[0];
      const version = (this.fileValidationVersions.get(input) || 0) + 1;
      this.fileValidationVersions.set(input, version);
      this.clearFilePreview(input);

      if (!file) {
        this.setFileError(input, '');
        return;
      }

      input.setCustomValidity('Validating image…');

      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        this.setFileError(input, this.dataset.invalidTypeMessage);
        return;
      }

      if (file.size > this.maxFileSize) {
        this.setFileError(input, this.dataset.fileSizeMessage);
        return;
      }

      try {
        const header = new Uint8Array(await file.slice(0, 12).arrayBuffer());
        if (this.fileValidationVersions.get(input) !== version) return;

        if (!this.hasValidSignature(file.type, header)) {
          this.setFileError(input, this.dataset.invalidTypeMessage);
          return;
        }

        const dimensions = await this.readImageDimensions(file);
        if (this.fileValidationVersions.get(input) !== version) return;

        if (dimensions.width * dimensions.height > this.maxPixels) {
          this.setFileError(input, this.dataset.fileDimensionsMessage);
          return;
        }

        this.setFileError(input, '');
        this.showFilePreview(input, file, dimensions);
      } catch (error) {
        this.setFileError(input, this.dataset.fileReadMessage);
      }
    }

    hasValidSignature(type, bytes) {
      if (type === 'image/jpeg') {
        return bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF;
      }

      return bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47 &&
        bytes[4] === 0x0D && bytes[5] === 0x0A && bytes[6] === 0x1A && bytes[7] === 0x0A;
    }

    readImageDimensions(file) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
          const dimensions = { width: image.naturalWidth, height: image.naturalHeight };
          URL.revokeObjectURL(url);
          resolve(dimensions);
        };
        image.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Image decode failed'));
        };
        image.src = url;
      });
    }

    setFileError(input, message) {
      input.setCustomValidity(message);
      input.closest('[data-file-input]').querySelector('[data-file-error]').textContent = message;
    }

    showFilePreview(input, file, dimensions) {
      const wrapper = input.closest('[data-file-input]');
      const preview = wrapper.querySelector('[data-image-preview]');
      const image = preview.querySelector('[data-preview-image]');
      const url = URL.createObjectURL(file);

      this.previewUrls.set(input, url);
      image.src = url;
      preview.querySelector('[data-preview-meta]').textContent = `${file.name} · ${dimensions.width}×${dimensions.height}px`;
      preview.hidden = false;
    }

    clearFilePreview(input) {
      const previousUrl = this.previewUrls.get(input);
      if (previousUrl) URL.revokeObjectURL(previousUrl);
      this.previewUrls.delete(input);

      const preview = input.closest('[data-file-input]').querySelector('[data-image-preview]');
      preview.hidden = true;
      preview.querySelector('[data-preview-image]').removeAttribute('src');
      preview.querySelector('[data-preview-meta]').textContent = '';
    }

    createReference() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }

      return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
  });
}
